import { observer } from 'mobx-react'
import React from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '../../../components/ModalSheet'
import { EditUserRoleDialogViewModel } from './EditUserRoleDialogViewModel'
import Button from '../../../components/Button'
import { Form, FormGroupContainer } from '../../../components/inputs'
import {
  CustomSelect,
  ItemSource,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from '../../../components/inputs/CustomSelect'

interface Props {
  vm: EditUserRoleDialogViewModel
}

export default observer(function EditUserRoleDialog({ vm }: Props) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () => ItemSource.static(vm.availableRoles),
      [vm.availableRoles],
    ),
  })

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={require('assets/images/img_missing-roles.svg')}
            alt="Illustration of switching on a toggle"
          />

          <ModalSheetContentTitle>Edit user role</ModalSheetContentTitle>

          <ModalSheetContentDescription>
            Assign a role for
            {vm.selectedUser ? (
              <b> {vm.selectedUser.displayName} </b>
            ) : (
              <b> {vm.selectedUserIds.length} selected users </b>
            )}
            in workspace <b>{vm.workspace.name}</b>.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form id="editUserRole" onSubmit={vm.submit}>
            <FormGroupContainer
              label="Select a new role"
              model={vm}
              field="selectedRole"
              error={vm.validation.getError('selectedRole')}
            >
              <CustomSelect
                {...selectProps}
                value={vm.selectedRole}
                onChange={vm.setSelectedRole}
                placeholder="Select new role"
                data-test="edit-user-role-select-role"
                multi={false}
                itemToString={(v) => String(v?.name)}
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => i.item.name}
                  />
                )}
              />
            </FormGroupContainer>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="primary"
            data-test="edit-user-role-apply-button"
            onClick={vm.submit}
            disabled={vm.submit.pending}
            loading={vm.submit.pending}
          >
            Apply
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
